/**
 * Quiz Landing Manager - Moon Moon Agency
 *
 * Landing page /pages/landing-quiz post-Typeform. Lit les params URL, remplace
 * [nom] dans le hero et injecte 2 sections MM-Edito-Product (main + alt) selon
 * une table de correspondance basée sur (morpho, conf, chaud).
 *
 * URL type :
 *   ?chaud=OUI&conf=EQUI&dos=NON&morpho=FINE&nom=Maxence&position=COTE#discover-product
 *
 * Table de correspondance : (morpho, conf, chaud) → { main: product_id, alt: product_id }.
 * Chaque slot [data-mm-quiz-slot] porte un JSON {product_id: handle} sérialisé côté
 * Liquid via les 4 product pickers de la section MM-Quiz-Product-Slot. Le JS résout
 * l'ID visé en handle puis fetch /products/<handle>?view=quiz-edito&sections=mm-edito-product.
 *
 * Le script est chargé par la section (2 fois quand main + alt sont sur la page) —
 * le guard `if (window.QuizLandingManager)` empeche le double-declare.
 */

if (typeof window.QuizLandingManager === 'undefined') {

/*********************************************************************************************
 * 🎯 QUIZ LANDING MANAGER
 */
const QuizLandingManager = {
	config: {
		selectors: {
			heroTitle: 'h1.mm-hero-small__title',
			slot: '[data-mm-quiz-slot]',
			slotTarget: '[data-mm-quiz-target]',
			slotLoader: '.mm-quiz-product-slot__loader',
			slotError: '[data-mm-quiz-error]',
		},
		namePlaceholder: '[nom]',
		titlePlaceholder: '[titre]',
		injectedTitleSelector: '.mm-edito-product__title',
		fetchViewSuffix: 'quiz-edito',
		// On fetche la PAGE COMPLETE (pas l'API Sections) : cette derniere rend la
		// section en isolation avec les defaults du schema, sans appliquer les
		// settings du template product.quiz-edito.json. La page complete respecte
		// bien le template alt (title=[titre], mode=resultat, etc.).
		injectedSectionSelector: '.mm-edito-product',
	},

	/**
	 * Table de correspondance (morpho, conf, chaud) → main/alt product IDs.
	 * Les cles morpho matchent les codes Typeform : FINE / MOY / FORT (pas
	 * MOYENNE/FORTE). MOY+EQUI et FORT+MOEL dependent de chaud ; les autres
	 * branches sont invariantes.
	 */
	QUIZ_MAP: {
		FINE: {
			MOEL: { main: '15648770785606', alt: '15648770195782' }, // Petit Nuage / Tout Doux
			EQUI: { main: '15648770654534', alt: '15648770490694' }, // Ideal / Grand Reve
		},
		MOY: {
			MOEL: { main: '15648770785606', alt: '15648770195782' }, // Petit Nuage / Tout Doux
			EQUI: {
				OUI: { main: '15648770490694', alt: '15648770654534' }, // Grand Reve / Ideal
				NON: { main: '15648770654534', alt: '15648770490694' }, // Ideal / Grand Reve
			},
		},
		FORT: {
			MOEL: {
				OUI: { main: '15648770785606', alt: '15648770490694' }, // Petit Nuage / Grand Reve
				NON: { main: '15648770785606', alt: '15648770654534' }, // Petit Nuage / Ideal
			},
			EQUI: { main: '15648770490694', alt: '15648770654534' }, // Grand Reve / Ideal
		},
	},

	init() {
		const slots = window.getElements(this.config.selectors.slot);
		if (!slots.length && !window.getElement(this.config.selectors.heroTitle)) return; // Guard : pas sur la landing quiz

		const params = this._parseParams();
		this._injectName(params.nom);
		this._resolveSlots(params);
		window.log('info', '🎯 [QUIZ_LANDING]', 'Initialized', { params });
	},

	/**
	 * Extraction des params URL avec normalisation vers upper-case (les valeurs
	 * du tableau metier sont FINE/MOEL/OUI, jamais case-mixed). Alias LIBRE
	 * → EQUI par decision metier (meme reco que confort equilibre).
	 */
	_parseParams() {
		const url = new URLSearchParams(window.location.search);
		let conf = (url.get('conf') || '').toUpperCase();
		if (conf === 'LIBRE') conf = 'EQUI';
		return {
			morpho: (url.get('morpho') || '').toUpperCase(),
			conf,
			chaud: (url.get('chaud') || '').toUpperCase(),
			nom: url.get('nom') || '',
		};
	},

	/**
	 * Remplace [nom] par le prenom en param dans le h1 du hero. Silencieux si
	 * le placeholder est absent ou le nom vide.
	 */
	_injectName(name) {
		if (!name) return;
		const title = window.getElement(this.config.selectors.heroTitle);
		if (!title) return;
		if (!title.textContent.includes(this.config.namePlaceholder)) return;
		title.textContent = title.textContent.replace(this.config.namePlaceholder, name);
	},

	/**
	 * Resout QUIZ_MAP[morpho][conf][chaud] avec fallback si la branche chaud
	 * n'existe pas. Retourne { main, alt } ou null si params insuffisants.
	 */
	_lookupProducts({ morpho, conf, chaud }) {
		if (!morpho || !conf) return null;
		const morphoNode = this.QUIZ_MAP[morpho];
		if (!morphoNode) return null;
		const confNode = morphoNode[conf];
		if (!confNode) return null;
		// Si le niveau conf contient directement main/alt, chaud n'est pas discriminant.
		if (confNode.main && confNode.alt) return confNode;
		// Sinon on descend au niveau chaud.
		if (chaud && confNode[chaud]) return confNode[chaud];
		// Fallback : premiere valeur du niveau chaud (evite un fetch qui echoue si chaud absent).
		const firstKey = Object.keys(confNode)[0];
		return firstKey ? confNode[firstKey] : null;
	},

	/**
	 * Pour chaque slot present dans le DOM, resout le product_id via la table,
	 * mappe vers un handle via le JSON serialise par la section Liquid, puis
	 * fetch et injecte le HTML.
	 */
	async _resolveSlots(params) {
		const target = this._lookupProducts(params);
		const slots = window.getElements(this.config.selectors.slot);

		for (const slot of slots) {
			const slotType = slot.dataset.mmQuizSlot; // 'main' | 'alt'
			if (!target || !target[slotType]) {
				this._showError(slot);
				continue;
			}

			const handlesMap = this._parseHandles(slot);
			const productId = target[slotType];
			const handle = handlesMap[productId];
			if (!handle) {
				window.log('warn', '🎯 [QUIZ_LANDING]', 'Handle unknown for product ID', { productId, slotType });
				this._showError(slot);
				continue;
			}

			await this._loadInto(slot, handle);
		}

		// Injection des sections = hauteur de la page qui change. LenisManager
		// (mm_animation.js) ecoute global:content-resized pour appeler resize()
		// et recalculer ses bornes, sinon on ne peut pas scroller jusqu'en bas.
		window.emit('global:content-resized', { source: 'quiz-landing' });
	},

	_parseHandles(slot) {
		try {
			return JSON.parse(slot.dataset.mmQuizHandles || '{}');
		} catch (err) {
			window.log('error', '🎯 [QUIZ_LANDING]', 'Invalid handles JSON', { error: err.message });
			return {};
		}
	},

	/**
	 * Fetch /products/<handle>?view=quiz-edito : on recupere la PAGE COMPLETE
	 * (template alt product.quiz-edito.json qui ne contient que MM-Edito-Product),
	 * puis on extrait la section via DOMParser. La Sections API (?sections=) rend
	 * la section en isolation avec les defaults du schema, sans appliquer les
	 * settings du template — d'ou le detour par la page complete.
	 */
	async _loadInto(slot, handle) {
		const target = slot.querySelector(this.config.selectors.slotTarget);
		const loader = slot.querySelector(this.config.selectors.slotLoader);

		try {
			const cacheBuster = `&_=${Date.now()}`;
			const url = `/products/${encodeURIComponent(handle)}?view=${this.config.fetchViewSuffix}${cacheBuster}`;
			const res = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
			if (!res.ok) throw new Error(`HTTP ${res.status}`);
			const fullHtml = await res.text();

			const doc = new DOMParser().parseFromString(fullHtml, 'text/html');
			const section = doc.querySelector(this.config.injectedSectionSelector);
			if (!section) throw new Error('Section MM-Edito-Product introuvable dans la page');

			// Injecte les stylesheets et les scripts globaux (Splide, mm_utils, etc.)
			// depuis le <head> de la page fetchee vers le document courant. La landing
			// quiz ne charge pas les CSS de MM-Edito-Product ni Splide — sans ca la
			// section injectee est completement unstyled et les carousels inertes.
			this._injectHeadAssets(doc);

			target.innerHTML = section.outerHTML;
			// Remplace [titre] dans le titre natif de la section injectee par le titre
			// custom du slot (setting data-mm-quiz-title, specifique reco vs alt).
			const slotTitle = slot.dataset.mmQuizTitle;
			if (slotTitle) {
				const titleEl = target.querySelector(this.config.injectedTitleSelector);
				if (titleEl && titleEl.textContent.includes(this.config.titlePlaceholder)) {
					titleEl.textContent = titleEl.textContent.replace(this.config.titlePlaceholder, slotTitle);
				}
			}

			// Les <script> injectes via innerHTML ne s'executent PAS (securite navigateur).
			// On re-cree chaque tag pour forcer l'execution — indispensable pour que
			// mm_edito_product.js s'initialise sur le HTML frais.
			this._executeScripts(target);

			// Trigger shopify:section:load : mm_edito_product.js (et autres modules MM)
			// ecoutent cet event pour re-init les carousels + selecteur de variantes sur
			// le DOM injecte. Sans ca les fleches et le <details> restent inertes.
			document.dispatchEvent(new CustomEvent('shopify:section:load', {
				detail: { sectionId: 'mm-edito-product', section: target },
			}));

			target.hidden = false;
			if (loader) loader.hidden = true;

			window.log('info', '🎯 [QUIZ_LANDING]', 'Slot loaded', { slot: slot.dataset.mmQuizSlot, handle });
		} catch (err) {
			window.log('error', '🎯 [QUIZ_LANDING]', 'Slot load failed', { handle, error: err.message });
			this._showError(slot);
		}
	},

	_showError(slot) {
		const loader = slot.querySelector(this.config.selectors.slotLoader);
		const errorEl = slot.querySelector(this.config.selectors.slotError);
		if (loader) loader.hidden = true;
		if (errorEl) errorEl.hidden = false;
	},

	/**
	 * Copie les <link rel="stylesheet"> et <script src> de la page fetchee qui
	 * ne sont pas deja presents dans le document courant. Dedupe par basename
	 * du fichier (asset_url ajoute un hash de cache-busting different par build,
	 * donc on compare le nom du fichier, pas l'URL complete).
	 */
	_injectHeadAssets(fetchedDoc) {
		const filenameOf = (url) => (url || '').split('?')[0].split('/').pop();

		const loadedCss = new Set(
			Array.from(document.querySelectorAll('link[rel="stylesheet"][href]'))
				.map((el) => filenameOf(el.href))
		);
		fetchedDoc.querySelectorAll('link[rel="stylesheet"][href]').forEach((link) => {
			const name = filenameOf(link.href);
			if (!name || loadedCss.has(name)) return;
			const clone = document.createElement('link');
			clone.rel = 'stylesheet';
			clone.href = link.href;
			document.head.appendChild(clone);
			loadedCss.add(name);
		});

		const loadedJs = new Set(
			Array.from(document.querySelectorAll('script[src]'))
				.map((el) => filenameOf(el.src))
		);
		fetchedDoc.querySelectorAll('script[src]').forEach((script) => {
			const name = filenameOf(script.src);
			if (!name || loadedJs.has(name)) return;
			const clone = document.createElement('script');
			Array.from(script.attributes).forEach((attr) => clone.setAttribute(attr.name, attr.value));
			document.head.appendChild(clone);
			loadedJs.add(name);
		});
	},

	/**
	 * Clone chaque <script> du HTML injecte dans un nouvel element : c'est la seule
	 * facon de forcer l'execution des scripts inseres via innerHTML (bloque par
	 * defaut cote navigateur). Preserve les attributs (src, defer, type, etc.).
	 */
	_executeScripts(container) {
		container.querySelectorAll('script').forEach((oldScript) => {
			const newScript = document.createElement('script');
			Array.from(oldScript.attributes).forEach((attr) => {
				newScript.setAttribute(attr.name, attr.value);
			});
			newScript.textContent = oldScript.textContent;
			oldScript.parentNode.replaceChild(newScript, oldScript);
		});
	},
};

// Expose sur window pour bloquer une seconde declaration (le guard en tete du fichier)
window.QuizLandingManager = QuizLandingManager;

// Auto-init
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => QuizLandingManager.init());
} else {
	QuizLandingManager.init();
}

} // fin du guard if (typeof window.QuizLandingManager === 'undefined')
